import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Mail, Lock, ArrowRight, Loader2, Eye, EyeOff, CheckCircle2, Shield } from 'lucide-react';
import axios from 'axios';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_ticketing-hub-18/artifacts/9kyejo6n_Screenshot%202026-04-08%20at%205.02.54%E2%80%AFPM.png";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const HERO_IMG = "https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800&q=80";
const TEAM_IMG = "https://images.unsplash.com/photo-1681949103006-70066fb25dfe?w=600&q=80";

const VALUE_PROPS = [
  { index: '01', title: 'Planboard & Kanban', desc: 'Grid-based task management with sticky columns, group-by-state, and inline editing.' },
  { index: '02', title: 'SLA Tracking', desc: 'Real-time dashboards with overdue alerts, KPI cards, and priority escalation.' },
  { index: '03', title: 'Process Automation', desc: 'Excel imports, auto-numbering, multi-assignee workflows, and sprint cycles.' },
  { index: '04', title: 'Resource Planning', desc: 'Unified projects, role-based access, and organisation-wide visibility.' },
];

const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();

  const [step, setStep] = useState('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Show Google SSO errors passed from AuthCallback
  useEffect(() => {
    if (location.state?.googleError) {
      setError(location.state.googleError);
      // Clear the state so error doesn't persist on refresh
      window.history.replaceState({}, '', '/login');
    }
  }, [location.state]);

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!email || !password) { setError('Please enter email and password'); return; }
    setLoading(true);
    setError('');
    try {
      await login(email, password);
      navigate('/');
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Invalid email or password');
    } finally { setLoading(false); }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!email) { setError('Please enter your email'); return; }
    setLoading(true);
    setError('');
    try {
      await axios.post(`${API}/auth/forgot-password`, { email });
      setSuccess('If this email is registered, a password reset link has been sent. Please check your inbox.');
    } catch { setError('Failed to process request'); } finally { setLoading(false); }
  };

  const handleGoogleLogin = () => {
    const redirectUrl = window.location.origin + '/';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="h-screen flex overflow-hidden" data-testid="login-page">
      {/* ======== LEFT PANEL — Branding & Imagery (55%) ======== */}
      <div className="hidden lg:flex lg:w-[55%] xl:w-[55%] bg-[#0A0A0A] relative overflow-hidden">
        <div className="w-full h-full p-8 xl:p-12 2xl:p-16 flex flex-col justify-between">
          {/* Top: Product branding */}
          <div className="flex-shrink-0">
            <div className="flex items-center gap-2.5 mb-6">
              <img src={LOGO_URL} alt="Intelvolks" className="h-7 object-contain opacity-60" />
              <span className="text-white/30 text-xs font-light">|</span>
              <span className="font-mono text-[10px] text-white/40 uppercase tracking-[0.25em]">by Intelvolks</span>
            </div>
            <h1 className="font-heading text-5xl xl:text-6xl 2xl:text-7xl font-black text-white tracking-tight leading-[0.95]">
              <span className="bg-gradient-to-r from-[#4D7AFF] via-[#6B9AFF] to-[#002FA7] bg-clip-text text-transparent">TASKS</span>
            </h1>
            <p className="text-white/40 text-sm xl:text-base mt-4 max-w-md leading-relaxed">
              Plan, track, and deliver projects with a platform engineered for operators — not analysts.
            </p>
          </div>

          {/* Middle: Bento Grid */}
          <div className="flex-1 flex flex-col justify-center py-6 max-h-[420px]" data-testid="login-bento-grid">
            <div className="grid grid-cols-3 gap-3" style={{ maxHeight: '200px' }}>
              {/* Large Image Card */}
              <div className="col-span-2 rounded-xl overflow-hidden relative group h-[200px]">
                <img src={HERO_IMG} alt="Dashboard Analytics"
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />
                <div className="absolute bottom-4 left-4 right-4">
                  <span className="font-mono text-[9px] text-white/50 uppercase tracking-[0.2em]">[01] KPI Visibility</span>
                  <p className="font-heading text-sm font-bold text-white mt-0.5">Real-time dashboards for the operator.</p>
                </div>
              </div>

              {/* Right stacked */}
              <div className="flex flex-col gap-3 h-[200px]">
                <div className="rounded-xl bg-[#002FA7] p-4 flex-1 flex flex-col justify-between">
                  <span className="font-mono text-[8px] text-white/40 uppercase tracking-[0.2em]">Built For</span>
                  <div>
                    <div className="font-heading text-2xl font-bold text-white">100K+</div>
                    <p className="text-white/50 text-[10px]">Students across India</p>
                  </div>
                </div>
                <div className="rounded-xl overflow-hidden relative flex-1 group">
                  <img src={TEAM_IMG} alt="Team Collaboration"
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                  <div className="absolute inset-0 bg-black/40" />
                  <div className="absolute bottom-2 left-3">
                    <span className="font-mono text-[8px] text-white/60 uppercase tracking-[0.2em]">Partnership</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Value Props Row */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5 mt-3" data-testid="login-value-props">
              {VALUE_PROPS.map((vp) => (
                <div key={vp.index}
                  className="rounded-lg border border-white/8 bg-white/[0.02] p-3 hover:bg-white/[0.05] transition-colors group">
                  <div className="flex items-center gap-1.5 mb-1">
                    <span className="font-mono text-[9px] text-[#4D7AFF] tracking-[0.15em]">[{vp.index}]</span>
                    <CheckCircle2 size={10} className="text-[#4D7AFF] opacity-0 group-hover:opacity-100 transition-opacity" />
                  </div>
                  <h3 className="font-heading text-[11px] font-bold text-white mb-0.5">{vp.title}</h3>
                  <p className="text-[10px] text-white/30 leading-relaxed line-clamp-2">{vp.desc}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Bottom Bar */}
          <div className="flex items-center justify-between pt-3 border-t border-white/8 flex-shrink-0">
            <div className="flex items-center gap-5">
              <span className="font-mono text-[9px] text-white/20 uppercase tracking-[0.2em]">Edutech</span>
              <span className="font-mono text-[9px] text-white/20 uppercase tracking-[0.2em]">Food-tech</span>
              <span className="font-mono text-[9px] text-white/20 uppercase tracking-[0.2em]">Enterprise</span>
            </div>
            <div className="flex items-center gap-1.5">
              <Shield size={10} className="text-white/20" />
              <span className="font-mono text-[9px] text-white/20 tracking-wider">SOC II Compliant</span>
            </div>
          </div>
        </div>
      </div>

      {/* ======== RIGHT PANEL — Login Form (45%) ======== */}
      <div className="w-full lg:w-[45%] xl:w-[45%] flex flex-col justify-center px-8 sm:px-12 lg:px-14 xl:px-20 2xl:px-28 py-8 bg-white relative">
        {/* Mobile-only logo */}
        <div className="lg:hidden mb-8 flex items-center gap-3">
          <img src={LOGO_URL} alt="Intelvolks" className="h-8 object-contain" data-testid="login-logo-mobile" />
          <div className="h-5 w-px bg-slate-200" />
          <span className="font-heading text-xl font-black tracking-tight bg-gradient-to-r from-[#002FA7] to-[#4D7AFF] bg-clip-text text-transparent">TASKS</span>
        </div>

        <div className="w-full max-w-[440px] mx-auto">
          {/* Desktop logo + product */}
          <div className="hidden lg:flex items-center gap-3 mb-8" data-testid="login-logo">
            <img src={LOGO_URL} alt="Intelvolks" className="h-8 object-contain" />
            <div className="h-5 w-px bg-slate-200" />
            <span className="font-heading text-xl font-black tracking-tight bg-gradient-to-r from-[#002FA7] to-[#4D7AFF] bg-clip-text text-transparent">TASKS</span>
          </div>

          {/* Heading */}
          <div className="mb-7">
            <h2 className="font-heading text-2xl xl:text-3xl font-black tracking-tight text-[#0A0A0A] leading-[1.1]">
              {step === 'login' ? 'Sign in to your workspace' : 'Reset your password'}
            </h2>
            <p className="mt-3 text-[15px] text-slate-500 leading-relaxed">
              {step === 'login' ? 'Manage projects, track progress, and deliver outcomes.' :
               'Enter your email and we\'ll send you a reset link.'}
            </p>
          </div>

          {/* Error / Success alerts */}
          {error && (
            <div className="mb-5 p-3.5 rounded-lg border text-sm font-medium bg-red-50 border-red-200 text-red-600" data-testid="login-error">{error}</div>
          )}
          {success && (
            <div className="mb-5 p-3.5 rounded-lg border text-sm font-medium bg-green-50 border-green-200 text-green-600" data-testid="login-success">{success}</div>
          )}

          {/* ===== LOGIN STEP ===== */}
          {step === 'login' && (
            <>
              <button
                onClick={handleGoogleLogin}
                className="w-full h-[48px] text-sm font-semibold rounded-lg flex items-center justify-center gap-3 border border-slate-200 text-slate-700 bg-white hover:bg-slate-50 hover:border-slate-300 transition-colors"
                data-testid="google-login-btn"
              >
                <svg width="18" height="18" viewBox="0 0 24 24">
                  <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
                  <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                  <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                  <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                </svg>
                Continue with Google
              </button>

              <div className="flex items-center gap-4 my-6">
                <div className="flex-1 border-t border-slate-200" />
                <span className="font-mono text-[10px] text-slate-400 uppercase tracking-[0.2em]">or sign in with email</span>
                <div className="flex-1 border-t border-slate-200" />
              </div>

              <form onSubmit={handleLogin} className="space-y-4">
                <div>
                  <label className="font-mono text-[10px] font-semibold uppercase tracking-[0.15em] text-slate-400 block mb-2">Email</label>
                  <div className="relative">
                    <Mail className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <input type="email" placeholder="you@company.com" value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="w-full pl-11 h-[48px] text-sm rounded-lg outline-none border border-slate-200 text-[#0A0A0A] bg-white focus:border-[#002FA7] focus:ring-2 focus:ring-[#002FA7]/10 transition-all"
                      data-testid="email-input" autoComplete="email" />
                  </div>
                </div>

                <div>
                  <label className="font-mono text-[10px] font-semibold uppercase tracking-[0.15em] text-slate-400 block mb-2">Password</label>
                  <div className="relative">
                    <Lock className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <input type={showPassword ? 'text' : 'password'} placeholder="Enter password" value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="w-full pl-11 pr-12 h-[48px] text-sm rounded-lg outline-none border border-slate-200 text-[#0A0A0A] bg-white focus:border-[#002FA7] focus:ring-2 focus:ring-[#002FA7]/10 transition-all"
                      data-testid="password-input" autoComplete="current-password" />
                    <button type="button" onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                      data-testid="toggle-password-btn">
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>

                <div className="flex justify-end">
                  <button type="button" onClick={() => { setStep('forgot'); setError(''); setSuccess(''); }}
                    className="text-sm text-[#002FA7] hover:underline font-medium" data-testid="forgot-password-link">
                    Forgot password?
                  </button>
                </div>

                <button type="submit" disabled={loading}
                  className="w-full h-[48px] text-sm font-semibold rounded-lg flex items-center justify-center gap-2 bg-[#002FA7] text-white hover:bg-[#002585] disabled:opacity-50 transition-all hover:-translate-y-[1px] active:translate-y-0"
                  data-testid="login-btn">
                  {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <>Sign In <ArrowRight className="h-4 w-4" /></>}
                </button>
              </form>
            </>
          )}

          {/* ===== FORGOT PASSWORD STEP ===== */}
          {step === 'forgot' && (
            <form onSubmit={handleForgotPassword} className="space-y-5">
              <div>
                <label className="font-mono text-[10px] font-semibold uppercase tracking-[0.15em] text-slate-400 block mb-2">Email Address</label>
                <div className="relative">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <input type="email" placeholder="you@company.com" value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full pl-11 h-[48px] text-sm rounded-lg outline-none border border-slate-200 text-[#0A0A0A] bg-white focus:border-[#002FA7] focus:ring-2 focus:ring-[#002FA7]/10 transition-all"
                    data-testid="forgot-email-input" />
                </div>
              </div>
              <button type="submit" disabled={loading}
                className="w-full h-[48px] text-sm font-semibold rounded-lg flex items-center justify-center gap-2 bg-[#002FA7] text-white hover:bg-[#002585] disabled:opacity-50 transition-all hover:-translate-y-[1px]"
                data-testid="forgot-submit-btn">
                {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <>Send Reset Link <ArrowRight className="h-4 w-4" /></>}
              </button>
              <button type="button" onClick={() => { setStep('login'); setError(''); setSuccess(''); }}
                className="w-full text-center text-sm text-slate-500 hover:text-slate-700" data-testid="back-to-login-link">
                Back to login
              </button>
            </form>
          )}

          {/* Footer */}
          <div className="mt-8 flex items-center justify-between">
            <p className="text-[11px] text-slate-400 font-mono tracking-wide">
              Intelvolks &middot; Software Development
            </p>
            <div className="flex items-center gap-1">
              <Shield size={10} className="text-slate-300" />
              <span className="text-[10px] text-slate-300 font-mono">SOC II</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
